import { DataType, FieldReferenceType, FieldTag, InputType, FieldLengthMeasurementUnit, FieldLifeCycleStatus, } from './field.interface';
import { Entity } from '../../../../domain/entity';
import { validationProvider } from '../../../../infrastructure/validation/validation.provider';
import { ApplicationError, ErrorCode } from '../../../../infrastructure/error/application-error';
import { FieldCreatedEvent } from './event/field-created.event';
import { FieldDeletedEvent } from './event/field-deleted.event';
import { FieldUpdatedEvent } from './event/field-updated.event';
import { FieldPutEvent } from './event/field-put.event';
import { FieldArchivedEvent } from './event/field-archived.event';
import projections from './projections-index';
import { getLogger } from '../../../../infrastructure/log/logger';
const logger = getLogger();
export class FieldEntity extends Entity {
    constructor(properties) {
        super(properties.id);
        this._name = validationProvider.validateAndTransformString(properties.name, {
            name: 'Name',
            required: true,
        });
        this._referenceId = validationProvider.validateAndTransformId(properties.referenceId, {
            name: 'Reference ID',
            required: true,
        });
        this._referenceType = validationProvider.validateAndTransformEnumValue(properties.referenceType, Object.values(FieldReferenceType), {
            name: 'Reference Type',
            required: true,
        });
        this._description = validationProvider.validateAndTransformString(properties.description, {
            name: 'Description',
        });
        this._inputType = validationProvider.validateAndTransformEnumValue(properties.inputType, Object.values(InputType), {
            name: 'Input Type',
            required: true,
        });
        this._dataType = validationProvider.validateAndTransformEnumValue(properties.dataType, Object.values(DataType), {
            name: 'Data Type',
            required: true,
        });
        this._allowMultipleValues = validationProvider.validateAndTransformBoolean(properties.allowMultipleValues, {
            name: 'Allow Multiple Values',
            transformTruthyAndFalsyToBoolean: true,
            transformStringToBoolean: true,
        });
        this._referenceTableFieldId = validationProvider.validateAndTransformId(properties.referenceTableFieldId, { name: 'Reference Table Field ID' });
        this._tag = validationProvider.validateAndTransformEnumValue(properties.tag, Object.values(FieldTag), { name: 'Field Tag' });
        this._orderIndex = validationProvider.validateAndTransformNumber(properties.orderIndex, {
            name: 'Order Index',
        });
        this._orderIndexInventoryMode = validationProvider.validateAndTransformNumber(properties.orderIndexInventoryMode, { name: 'Inventory Mode Order Index' });
        this._lengthMeasurementUnit = validationProvider.validateAndTransformEnumValue(properties.lengthMeasurementUnit, Object.values(FieldLengthMeasurementUnit), { name: 'Length Measurement Unit' });
        this._defaultValue = validationProvider.validateAndTransformString(properties.defaultValue, {
            name: 'Default Value',
        });
        this._required = validationProvider.validateAndTransformBoolean(properties.required, {
            name: 'Required',
            transformTruthyAndFalsyToBoolean: true,
            transformStringToBoolean: true,
        });
        this._readOnly = validationProvider.validateAndTransformBoolean(properties.readOnly, {
            name: 'Read Only',
            transformTruthyAndFalsyToBoolean: true,
            transformStringToBoolean: true,
        });
        this._immutable = validationProvider.validateAndTransformBoolean(properties.immutable, {
            name: 'Immutable',
            transformTruthyAndFalsyToBoolean: true,
            transformStringToBoolean: true,
        });
        this._maxLength = validationProvider.validateAndTransformNumber(properties.maxLength, {
            name: 'Max Length',
        });
        this._shouldTriggerAutoUpdate = validationProvider.validateAndTransformBoolean(properties.shouldTriggerAutoUpdate, {
            name: 'Should Trigger Auto Update',
            transformTruthyAndFalsyToBoolean: true,
            transformStringToBoolean: true,
        });
        this._syncFieldUuids = properties.syncFieldUuids || [];
        this._tp2FieldName = validationProvider.validateAndTransformString(properties.tp2FieldName, {
            name: 'Tp2 Field Name',
        });
        if (Array.isArray(properties.categories)) {
            this._categories = properties.categories.map(category => validationProvider.validateAndTransformId(category, {
                name: 'Category',
            }));
        }
        else {
            this._categories = [];
        }
        this._lifeCycleStatus =
            validationProvider.validateAndTransformEnumValue(properties.lifeCycleStatus, Object.values(FieldLifeCycleStatus), { name: 'Life Cycle Status' }) || FieldLifeCycleStatus.ACTIVE;
    }
    create(metadata) {
        return new FieldCreatedEvent({
            id: this.id,
            referenceId: this.referenceId,
            referenceType: this.referenceType,
            name: this.name,
            description: this.description,
            inputType: this.inputType,
            dataType: this.dataType,
            allowMultipleValues: this.allowMultipleValues,
            referenceTableFieldId: this.referenceTableFieldId,
            tag: this.tag,
            orderIndex: this.orderIndex,
            orderIndexInventoryMode: this.orderIndexInventoryMode,
            lengthMeasurementUnit: this.lengthMeasurementUnit,
            defaultValue: this.defaultValue,
            required: this.required,
            readOnly: this.readOnly,
            immutable: this.immutable,
            maxLength: this.maxLength,
            shouldTriggerAutoUpdate: this.shouldTriggerAutoUpdate,
            syncFieldUuids: this.syncFieldUuids,
            tp2FieldName: this.tp2FieldName,
            categories: this.categories,
        }, metadata);
    }
    update({ name, description, inputType, referenceTableFieldId, tag, orderIndex, orderIndexInventoryMode, lengthMeasurementUnit, defaultValue, required, readOnly, immutable, maxLength, syncFieldUuids, shouldTriggerAutoUpdate, }, metadata) {
        if (name !== undefined) {
            this.name = validationProvider.validateAndTransformString(name, {
                name: 'Name',
                required: true,
            });
        }
        if (description !== undefined) {
            this.description = validationProvider.validateAndTransformString(description, {
                name: 'Description',
            });
        }
        if (inputType !== undefined) {
            this.inputType = validationProvider.validateAndTransformEnumValue(inputType, Object.values(InputType), {
                name: 'Input Type',
                required: true,
            });
        }
        if (referenceTableFieldId !== undefined) {
            this.referenceTableFieldId = validationProvider.validateAndTransformId(referenceTableFieldId, { name: 'Reference Table Field ID' });
        }
        if (tag !== undefined) {
            this.tag = validationProvider.validateAndTransformEnumValue(tag, Object.values(FieldTag), {
                name: 'Field Tag',
            });
        }
        if (orderIndex !== undefined) {
            this.orderIndex = validationProvider.validateAndTransformNumber(orderIndex, {
                name: 'Order Index',
            });
        }
        if (orderIndexInventoryMode !== undefined) {
            this.orderIndexInventoryMode = validationProvider.validateAndTransformNumber(orderIndexInventoryMode, { name: 'Order Index' });
        }
        if (lengthMeasurementUnit !== undefined) {
            this.lengthMeasurementUnit = validationProvider.validateAndTransformEnumValue(lengthMeasurementUnit, Object.values(FieldLengthMeasurementUnit), { name: 'Length Measurement Unit' });
        }
        if (defaultValue !== undefined) {
            this.defaultValue = validationProvider.validateAndTransformString(defaultValue, {
                name: 'Default Value',
            });
        }
        if (required !== undefined) {
            this.required = validationProvider.validateAndTransformBoolean(required, {
                name: 'Required',
                transformTruthyAndFalsyToBoolean: true,
                transformStringToBoolean: true,
            });
        }
        if (readOnly !== undefined) {
            this.readOnly = validationProvider.validateAndTransformBoolean(readOnly, {
                name: 'Read Only',
                transformTruthyAndFalsyToBoolean: true,
                transformStringToBoolean: true,
            });
        }
        if (immutable !== undefined) {
            this.immutable = validationProvider.validateAndTransformBoolean(immutable, {
                name: 'Immutable',
                transformTruthyAndFalsyToBoolean: true,
                transformStringToBoolean: true,
            });
        }
        if (maxLength !== undefined) {
            this.maxLength = validationProvider.validateAndTransformNumber(maxLength, {
                name: 'Max Length',
            });
        }
        if (shouldTriggerAutoUpdate !== undefined) {
            this.shouldTriggerAutoUpdate = validationProvider.validateAndTransformBoolean(shouldTriggerAutoUpdate, {
                name: 'Should Trigger Auto Update',
                transformTruthyAndFalsyToBoolean: true,
                transformStringToBoolean: true,
            });
        }
        if (syncFieldUuids !== undefined) {
            this.syncFieldUuids = syncFieldUuids;
        }
        return new FieldUpdatedEvent({
            id: this.id,
            name: this.name,
            description: this.description,
            inputType: this.inputType,
            referenceTableFieldId: this.referenceTableFieldId,
            tag: this.tag,
            orderIndex: this.orderIndex,
            orderIndexInventoryMode: this.orderIndexInventoryMode,
            lengthMeasurementUnit: this.lengthMeasurementUnit,
            defaultValue: this.defaultValue,
            required: this.required,
            readOnly: this.readOnly,
            immutable: this.immutable,
            maxLength: this.maxLength,
            shouldTriggerAutoUpdate: this.shouldTriggerAutoUpdate,
            syncFieldUuids: this.syncFieldUuids,
        }, metadata);
    }
    delete(metadata) {
        return new FieldDeletedEvent({
            id: this.id,
            deleted: true,
        }, metadata);
    }
    archive(metadata) {
        if (this._lifeCycleStatus === FieldLifeCycleStatus.DEPRECATED) {
            throw new ApplicationError({
                code: ErrorCode.ERR_VALIDATION,
                messageExternal: `Field is already archived: ${this.id}`,
            });
        }
        this._lifeCycleStatus = FieldLifeCycleStatus.DEPRECATED;
        return new FieldArchivedEvent({
            id: this.id,
            lifeCycleStatus: FieldLifeCycleStatus.DEPRECATED,
        }, metadata);
    }
    put(properties, metadata) {
        this._name = validationProvider.validateAndTransformString(properties.name, {
            name: 'Name',
            required: true,
        });
        this._referenceId = validationProvider.validateAndTransformId(properties.referenceId, {
            name: 'Reference ID',
            required: true,
        });
        this._referenceType = validationProvider.validateAndTransformEnumValue(properties.referenceType, Object.values(FieldReferenceType), { name: 'Reference Type', required: true });
        this._description = validationProvider.validateAndTransformString(properties.description, {
            name: 'Description',
        });
        this._inputType = validationProvider.validateAndTransformEnumValue(properties.inputType, Object.values(InputType), { name: 'Input Type', required: true });
        this._dataType = validationProvider.validateAndTransformEnumValue(properties.dataType, Object.values(DataType), { name: 'Data Type', required: true });
        this._allowMultipleValues = validationProvider.validateAndTransformBoolean(properties.allowMultipleValues, {
            name: 'Allow Multiple Values',
            transformTruthyAndFalsyToBoolean: true,
            transformStringToBoolean: true,
        });
        this._referenceTableFieldId = validationProvider.validateAndTransformId(properties.referenceTableFieldId, { name: 'Reference Table Field ID' });
        if (properties.tag !== undefined) {
            this._tag = validationProvider.validateAndTransformEnumValue(properties.tag, Object.values(FieldTag), { name: 'Field Tag' });
        }
        this._orderIndex = validationProvider.validateAndTransformNumber(properties.orderIndex, {
            name: 'Order Index',
        });
        this._orderIndexInventoryMode = validationProvider.validateAndTransformNumber(properties.orderIndexInventoryMode, { name: 'Inventory Mode Order Index' });
        this._lengthMeasurementUnit = validationProvider.validateAndTransformEnumValue(properties.lengthMeasurementUnit, Object.values(FieldLengthMeasurementUnit), { name: 'Length Measurement Unit' });
        this._defaultValue = validationProvider.validateAndTransformString(properties.defaultValue, {
            name: 'Default Value',
        });
        this._required = validationProvider.validateAndTransformBoolean(properties.required, {
            name: 'Required',
            transformTruthyAndFalsyToBoolean: true,
            transformStringToBoolean: true,
        });
        this._readOnly = validationProvider.validateAndTransformBoolean(properties.readOnly, {
            name: 'Read Only',
            transformTruthyAndFalsyToBoolean: true,
            transformStringToBoolean: true,
        });
        this._immutable = validationProvider.validateAndTransformBoolean(properties.immutable, {
            name: 'Immutable',
            transformTruthyAndFalsyToBoolean: true,
            transformStringToBoolean: true,
        });
        this._maxLength = validationProvider.validateAndTransformNumber(properties.maxLength, {
            name: 'Max Length',
        });
        this._shouldTriggerAutoUpdate = validationProvider.validateAndTransformBoolean(properties.shouldTriggerAutoUpdate, {
            name: 'Should Trigger Auto Update',
            transformTruthyAndFalsyToBoolean: true,
            transformStringToBoolean: true,
        });
        this._syncFieldUuids = properties.syncFieldUuids || [];
        this._tp2FieldName = validationProvider.validateAndTransformString(properties.tp2FieldName, {
            name: 'Tp2 Field Name',
        });
        if (Array.isArray(properties.categories)) {
            this._categories = properties.categories.map(category => validationProvider.validateAndTransformId(category, {
                name: 'Category',
            }));
        }
        else {
            this._categories = [];
        }
        return new FieldPutEvent({
            id: this.id,
            referenceId: this.referenceId,
            referenceType: this.referenceType,
            name: this.name,
            description: this.description,
            inputType: this.inputType,
            dataType: this.dataType,
            allowMultipleValues: this.allowMultipleValues,
            referenceTableFieldId: this.referenceTableFieldId,
            tag: this.tag,
            orderIndex: this.orderIndex,
            orderIndexInventoryMode: this.orderIndexInventoryMode,
            lengthMeasurementUnit: this.lengthMeasurementUnit,
            defaultValue: this.defaultValue,
            required: this.required,
            readOnly: this.readOnly,
            immutable: this.immutable,
            maxLength: this.maxLength,
            shouldTriggerAutoUpdate: this.shouldTriggerAutoUpdate,
            syncFieldUuids: this.syncFieldUuids,
            tp2FieldName: this.tp2FieldName,
            categories: this.categories,
        }, metadata);
    }
    static getFieldFromId(fieldId, fields) {
        const field = fields.find(field => field.id === fieldId);
        if (!field) {
            const message = `Field does not exist: ${fieldId}`;
            logger.error(message);
            throw new ApplicationError({
                code: ErrorCode.ERR_NOT_FOUND,
                messageExternal: message,
            });
        }
        return field;
    }
    static restoreAggregateState(events, snapshot) {
        return super.buildAggregateState(events, projections, snapshot);
    }
    get name() {
        return this._name || '';
    }
    set name(name) {
        this._name = name;
    }
    get referenceId() {
        return this._referenceId;
    }
    get referenceType() {
        return this._referenceType;
    }
    set description(description) {
        this._description = description;
    }
    get description() {
        return this._description;
    }
    set inputType(inputType) {
        this._inputType = inputType;
    }
    get inputType() {
        return this._inputType;
    }
    get dataType() {
        return this._dataType;
    }
    set dataType(dataType) {
        this._dataType = dataType;
    }
    get allowMultipleValues() {
        return this._allowMultipleValues;
    }
    set allowMultipleValues(allowMultipleValues) {
        this._allowMultipleValues = allowMultipleValues;
    }
    get referenceTableFieldId() {
        return this._referenceTableFieldId;
    }
    set referenceTableFieldId(referenceTableFieldId) {
        this._referenceTableFieldId = referenceTableFieldId;
    }
    get tag() {
        return this._tag;
    }
    set tag(tag) {
        this._tag = tag;
    }
    get orderIndex() {
        return this._orderIndex;
    }
    set orderIndex(orderIndex) {
        this._orderIndex = orderIndex;
    }
    get orderIndexInventoryMode() {
        return this._orderIndexInventoryMode;
    }
    set orderIndexInventoryMode(orderIndexInventoryMode) {
        this._orderIndexInventoryMode = orderIndexInventoryMode;
    }
    get lengthMeasurementUnit() {
        return this._lengthMeasurementUnit;
    }
    set lengthMeasurementUnit(lengthMeasurementUnit) {
        this._lengthMeasurementUnit = lengthMeasurementUnit;
    }
    get defaultValue() {
        return this._defaultValue;
    }
    set defaultValue(defaultValue) {
        this._defaultValue = defaultValue;
    }
    get required() {
        return this._required;
    }
    set required(required) {
        this._required = required;
    }
    get readOnly() {
        return this._readOnly;
    }
    set readOnly(readOnly) {
        this._readOnly = readOnly;
    }
    get immutable() {
        return this._immutable;
    }
    set immutable(immutable) {
        this._immutable = immutable;
    }
    get maxLength() {
        return this._maxLength;
    }
    set maxLength(maxLength) {
        this._maxLength = maxLength;
    }
    get shouldTriggerAutoUpdate() {
        return this._shouldTriggerAutoUpdate;
    }
    set shouldTriggerAutoUpdate(shouldTriggerAutoUpdate) {
        this._shouldTriggerAutoUpdate = shouldTriggerAutoUpdate;
    }
    get syncFieldUuids() {
        return this._syncFieldUuids;
    }
    set syncFieldUuids(syncFieldUuids) {
        this._syncFieldUuids = syncFieldUuids;
    }
    get tp2FieldName() {
        return this._tp2FieldName;
    }
    set tp2FieldName(tp2FieldName) {
        this._tp2FieldName = tp2FieldName;
    }
    get categories() {
        return this._categories;
    }
    set categories(categories) {
        this._categories = categories;
    }
    get lifeCycleStatus() {
        return this._lifeCycleStatus;
    }
    set lifeCycleStatus(lifeCycleStatus) {
        this._lifeCycleStatus = lifeCycleStatus;
    }
    static sortFields(fields) {
        return [...fields].sort((a, b) => {
            if (a.orderIndex === undefined && b.orderIndex === undefined) {
                return 0;
            }
            else if (a.orderIndex === undefined && b.orderIndex !== undefined) {
                return 1;
            }
            else if (a.orderIndex !== undefined && b.orderIndex === undefined) {
                return -1;
            }
            return a.orderIndex - b.orderIndex;
        });
    }
    static findFieldFromId(fields, fieldId) {
        return fields.find(f => f.id === fieldId);
    }
}
//# sourceMappingURL=field.model.js.map